// D57-A — the utility carrier brain (Mark/Dill GDC-2010 pattern, verified in FAFI-Bot-AI-Research.md).
// Instead of a first-match-wins branch chain, the carrier SCORES every option every think:
//   shoot · pass(each mate) · through(each running mate) · dribble(5 directions) · clear
// via 0..1 response curves over xG, progression, receiver openness, lane time-to-intercept margin
// (Spearman-lite), and space ahead — then picks the max. Two feel mechanisms from the canon: an
// INERTIA bonus on the standing choice (no decision ping-pong) and seeded NOISE per option (never
// metronomic; scaled up for dumber difficulty profiles). The D42 human-support doctrine and D44 call
// obedience are score MULTIPLIERS here (HUMAN_MUL / CALLER_MUL) — the safety-critical rules (own-box
// defusal, in-opponent-box shot) stay as hard branches in actPlayer, ABOVE this brain.
// Pure + deterministic: fixed evaluation order, all randomness from the passed aiRng.

import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';
import { xg } from './xg';
import { rngFloat, type RngHolder } from '../math/rng';
import { laneMargin } from '../sim/longPass';
import type { TeamPlan } from './plan';
import type { DifficultyProfile } from './difficulty';

export interface CarrierChoice {
  key: string; // stable identity → inertia ("pass:7", "dribble:2", …)
  /** D114: `lob` and `cross` are both THROUGH + LOFT on the wire — the difference is WHEN they win, not
   *  what they emit. A lob is "the ground lane is shut, go over it"; a cross is "I'm wide in the final
   *  third and someone is attacking the box". The sim tells them apart by geometry, not by intent. */
  kind: 'shoot' | 'pass' | 'through' | 'dribble' | 'clear' | 'lob' | 'cross';
  target: EntityId | null; // pass/through/lob/cross receiver
  dir: number; // dribble/clear direction; kicks recompute their live aim at execution
  score: number;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const lin = (v: number, a: number, b: number): number => clamp01((v - a) / (b - a));
const logistic = (x: number, mid: number, k: number): number => 1 / (1 + Math.exp(-k * (x - mid)));
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
const angleTo = (from: Vec2, to: Vec2): number => Math.atan2(to.y - from.y, to.x - from.x);

/**
 * Spearman-lite lane safety: for the pass from→to at `meanSpeed`, the WORST time margin over all
 * opponents — (time the opponent needs to reach his closest lane point) − (time the ball passes it),
 * plus a per-metre relief for a defender who is merely NEAR the lane rather than on it. Mapped through
 * a logistic to 0 (certain interception) … 1 (untouchable).
 *
 * D112: the margin itself moved into `sim/longPass.ts`. It has to live inside the purity boundary
 * because `applyKicks` scores lanes for HUMAN long balls now, and sim/ cannot import ai/. Identical
 * arithmetic — this is the same function it always was, just no longer owned by the bot brain.
 */
export function laneSafety(state: MatchState, team: number, from: Vec2, to: Vec2, meanSpeed: number): number {
  const worst = laneMargin(state, team, from, to, meanSpeed);
  if (worst === Infinity) return 1;
  return logistic(worst, -0.1, 8);
}

/** Shot sight: 1 with a clean line, →0 with an outfield body ON it (a blocker stops a shot regardless
 *  of ball speed — the D52 swept collision is real). Keepers never binary-block (corners beat them). */
function shotSight(state: MatchState, p: PlayerState, goal: Vec2): number {
  const gx = goal.x - p.pos.x;
  const gy = goal.y - p.pos.y;
  const len = Math.hypot(gx, gy);
  if (len < 1e-6) return 1;
  const ux = gx / len;
  const uy = gy / len;
  let sight = 1;
  for (const o of state.players) {
    if (o.team === p.team || o.sentOff || o.isGk) continue;
    const rx = o.pos.x - p.pos.x;
    const ry = o.pos.y - p.pos.y;
    const along = rx * ux + ry * uy;
    if (along <= 0 || along >= len) continue;
    const perp = Math.abs(rx * uy - ry * ux);
    sight = Math.min(sight, lin(perp, 0.45, 1.8));
  }
  return sight;
}

/** Open space along a dribble ray: distance to the nearest opponent within a ±35° cone of `dir`. */
/** D132: exported as `spaceAheadOf` so the dribble-sprint gate reads the SAME open ray the carrier brain
 *  scored its dribble option with — one notion of "open", not two that can disagree. */
export function spaceAhead(state: MatchState, p: PlayerState, dir: number): number {
  let best = 14;
  for (const o of state.players) {
    if (o.team === p.team || o.sentOff) continue;
    const d = dist(p.pos, o.pos);
    if (d > 14) continue;
    let a = (angleTo(p.pos, o.pos) - dir) % (Math.PI * 2);
    if (a > Math.PI) a -= Math.PI * 2;
    else if (a < -Math.PI) a += Math.PI * 2;
    if (Math.abs(a) < 0.6) best = Math.min(best, d);
  }
  return best;
}

function nearestOppDist(state: MatchState, pos: Vec2, team: number): number {
  let best = Infinity;
  for (const o of state.players) {
    if (o.team === team || o.sentOff) continue;
    best = Math.min(best, dist(pos, o.pos));
  }
  return best;
}

/**
 * Score every carrier option and return the best. `lastKey` earns the inertia bonus; all draws come
 * from `rng` in a FIXED order (shoot, passes asc id, throughs asc id, dribbles, clear) — deterministic.
 */
export function decideCarrier(
  state: MatchState,
  id: EntityId,
  plan: TeamPlan,
  cfg: SimConfig,
  prof: DifficultyProfile,
  rng: RngHolder,
  lastKey: string | null,
  returnTo: EntityId | null = null, // D59: the mate who JUST fed me — return passes to him are suppressed
): CarrierChoice {
  const U = cfg.ai.UTIL;
  const p = state.players[id]!;
  const size = state.mode.teamSize;
  const pitch = PITCH[size];
  const dir = state.attackDir[p.team]!;
  const goalX = (dir * pitch.length) / 2;
  const distToGoal = Math.hypot(goalX - p.pos.x, p.pos.y);
  // D116: TWO-SIDED. `Math.max(1, mul)` threw away the whole sharper half of the ladder — a level-5 bot
  // deliberated exactly as sloppily as a level-3 one. A weak player's real error is picking the wrong
  // ball, not shanking the right one, so this is the channel that carries most of "he's worse than you":
  // shoot/dribble/clear ride `aimNoiseMul`, the pass family rides `passErrorMul` (which the D34 soft-lock
  // leaves no other way to express — see difficulty.ts::passElevNoise).
  const noiseAmp = U.NOISE * prof.aimNoiseMul;
  const passNoiseAmp = U.NOISE * prof.passErrorMul;
  const humanNear = [...plan.humans].some((h) => {
    const hp = state.players[h];
    return hp !== undefined && hp.id !== id && !hp.sentOff && dist(p.pos, hp.pos) < cfg.ai.HUMAN_NEAR_DIST;
  });

  let best: CarrierChoice = { key: 'dribble:0', kind: 'dribble', target: null, dir: angleTo(p.pos, { x: goalX, y: p.pos.y * 0.5 }), score: -1 };
  let second: CarrierChoice | null = null;
  const PASS_KINDS = new Set(['pass', 'through', 'lob', 'cross']);
  const consider = (c: Omit<CarrierChoice, 'score'>, raw: number): void => {
    // one draw per option, in the same fixed order as ever — only the amplitude is profile-dependent
    let score = raw * (1 + (rngFloat(rng) * 2 - 1) * (PASS_KINDS.has(c.kind) ? passNoiseAmp : noiseAmp));
    if (lastKey !== null && c.key === lastKey) score *= U.INERTIA_MUL;
    if (score > best.score) {
      second = best.score >= 0 ? best : second;
      best = { ...c, score };
    } else if (second === null || score > second.score) {
      second = { ...c, score };
    }
  };

  // 1 · SHOOT — xG renormalized to the utility axis (raw xG ≈ 0.3 at 9 m is a GREAT chance and must
  // outrank a comfortable dribble) × clear-ish sight of goal (the in-box hard rule lives above this brain)
  {
    const gq = lin(xg(p.pos, goalX, cfg), 0.04, 0.35);
    const sight = shotSight(state, p, { x: goalX, y: 0 }); // bodies on the line block at ANY ball speed
    const serve = humanNear ? U.SERVE_MUL : 1; // D42: with a human close, the finish is HIS — the bot serves
    consider({ key: 'shoot', kind: 'shoot', target: null, dir: angleTo(p.pos, { x: goalX, y: 0 }) }, U.SHOOT * gq * serve * (0.15 + 0.85 * sight));
  }

  // 2/3 · PASS and THROUGH per teammate (ascending id — normative draw order)
  for (const t of state.players) {
    if (t.team !== p.team || t.id === id || t.sentOff) continue;
    const d = dist(p.pos, t.pos);
    if (d < 2) continue; // not a pass, a hand-over
    const isHuman = plan.humans.has(t.id);
    const open = lin(nearestOppDist(state, t.pos, p.team), 1, 7);
    // D59: the human bias is EARNED, not flat — a marked human is just a marked player; the full
    // HUMAN_MUL applies only when he's actually open (D42's serve doctrine, made conditional).
    const human = isHuman ? 1 + (U.HUMAN_MUL - 1) * open : 1;
    const caller = isHuman && t.calling ? U.CALLER_MUL : 1;
    // D59: no instant ping-pong — the mate who just played me the ball must MOVE before he's a
    // target again; an explicit CALL exempts (a deliberate one-two is football, a reflex return isn't).
    const ret = t.id === returnTo && !t.calling ? U.RETURN_MUL : 1;
    const targetInOwnBox = dir * (t.pos.x + (dir * pitch.length) / 2) < pitch.penaltyBoxDepth && Math.abs(t.pos.y) < pitch.penaltyBoxHalfWidth;
    const boxPenalty = targetInOwnBox ? 0.25 : 1; // never play a mate into trouble at the own goal
    const progression = lin(distToGoal - Math.hypot(goalX - t.pos.x, t.pos.y), -8, 12);
    const safety = laneSafety(state, p.team, p.pos, t.pos, 14);
    consider(
      { key: `pass:${t.id}`, kind: 'pass', target: t.id, dir: angleTo(p.pos, t.pos) },
      U.PASS * human * caller * ret * boxPenalty * (0.25 + 0.75 * open) * (0.3 + 0.7 * progression) * (0.2 + 0.8 * safety),
    );
    // through: only for a mate already RUNNING upfield — lead the run (the D49 sim lead does the rest)
    if (dir * t.vel.x > 1.5) {
      const led = { x: t.pos.x + t.vel.x * 0.7, y: t.pos.y + t.vel.y * 0.7 };
      const ledSafety = laneSafety(state, p.team, p.pos, led, 16);
      const ledProg = lin(distToGoal - Math.hypot(goalX - led.x, led.y), -4, 14);
      consider(
        { key: `through:${t.id}`, kind: 'through', target: t.id, dir: angleTo(p.pos, led) },
        // NOTE: no `ret` here — a feeder BURSTING upfield after his pass is the give-and-go, not the
        // reflex return; leading his run is exactly what the suppression is trying to teach.
        U.THROUGH * human * caller * (0.3 + 0.7 * ledProg) * (0.2 + 0.8 * ledSafety),
      );
      // D114 (HB-E) · LOB — the same runner, over the top. Its value is the MIRROR of the through-ball's:
      // a lofted ball's lane is clean by construction (it flies over the bodies), so the lob is worth
      // playing exactly when the FLAT lane is not. Scoring it on (1 − ledSafety) is what stops a bot from
      // chipping when a simple ground pass was on, and what makes it find the ball over a packed defence.
      consider(
        { key: `lob:${t.id}`, kind: 'lob', target: t.id, dir: angleTo(p.pos, led) },
        cfg.ai.LOB_UTIL * human * caller * (0.3 + 0.7 * ledProg) * (0.25 + 0.75 * (1 - ledSafety)),
      );
    }
  }

  // 3b · D114 (HB-E) · CROSS — a WIDE carrier in the final third with someone attacking the box. Not a
  // separate verb (HB-P4): it emits THROUGH + LOFT, and `pickLongTarget`'s box-biased ladder does the
  // aiming. Gated on geometry the player can read off the pitch, so a bot crosses when a human would.
  {
    const attackX01 = (dir * p.pos.x + pitch.length / 2) / pitch.length;
    const wide = Math.abs(p.pos.y) >= (pitch.width / 2) * cfg.ai.CROSS.WIDE_Y;
    if (attackX01 >= cfg.ai.CROSS.MIN_ATTACK_X && wide) {
      let runner: PlayerState | null = null;
      let bestOpen = -Infinity;
      for (const t of state.players) {
        if (t.team !== p.team || t.id === id || t.sentOff) continue;
        const tAttack01 = (dir * t.pos.x + pitch.length / 2) / pitch.length;
        if (tAttack01 < cfg.ai.CROSS.MIN_ATTACK_X || Math.abs(t.pos.y) > pitch.penaltyBoxHalfWidth) continue;
        const open = nearestOppDist(state, t.pos, p.team);
        if (open > bestOpen) {
          bestOpen = open;
          runner = t;
        }
      }
      if (runner) {
        // A cross is a delivery INTO a contest, so the runner's openness barely matters (0.7 + 0.3) —
        // what decides it is whether the GROUND route to him is shut, because that is the whole reason
        // to put the ball in the air. With a clean flat lane the plain PASS outscores it, which is
        // right: crossing to an unmarked man you could simply pass to is hoofing it.
        const open = lin(bestOpen, 1, 7);
        const flat = laneSafety(state, p.team, p.pos, runner.pos, 16);
        consider(
          { key: `cross:${runner.id}`, kind: 'cross', target: runner.id, dir: angleTo(p.pos, runner.pos) },
          cfg.ai.CROSS.UTIL * (0.7 + 0.3 * open) * (0.5 + 0.5 * (1 - flat)),
        );
      }
    }
  }

  // 4 · DRIBBLE — five directions fanned around goal-ward, scored by space × progression
  {
    const toGoal = angleTo(p.pos, { x: goalX, y: p.pos.y * 0.5 });
    const handoff = humanNear ? U.HANDOFF_MUL : 1; // D42: with a human close, bots look to serve, not to run
    const ownThird = dir * p.pos.x < -pitch.length / 6 ? 0.55 : 1;
    for (let k = 0; k < 5; k++) {
      const off = [0, 0.6, -0.6, 1.2, -1.2][k]!;
      const dd = toGoal + off;
      const space = lin(spaceAhead(state, p, dd), 1.5, 10);
      const prog = clamp01(Math.cos(off)); // straighter = more progressive
      consider({ key: `dribble:${k}`, kind: 'dribble', target: null, dir: dd }, U.DRIBBLE * handoff * ownThird * space * (0.35 + 0.65 * prog));
    }
  }

  // 5 · CLEAR — own third only: hoof it to the emptier flank when nothing better exists
  if (dir * p.pos.x < -pitch.length / 6) {
    const pressure = lin(6 - nearestOppDist(state, p.pos, p.team), 0, 5);
    const flankY = (p.pos.y >= 0 ? -1 : 1) * (pitch.width / 2) * 0.6;
    consider(
      { key: 'clear', kind: 'clear', target: null, dir: angleTo(p.pos, { x: dir * pitch.length * 0.25, y: flankY }) },
      U.CLEAR * (0.25 + 0.75 * pressure),
    );
  }
  // D136 — the WRONG BALL. A weak player's real error is picking the wrong option, not shanking the
  // right one, and until now the only fallibility channel was score NOISE, which is symmetric and
  // therefore invisible: it never produces the legible "why did he play THAT?" moment. One draw per
  // carrier think, AFTER every option has been scored, so it does not perturb the fixed draw order
  // above. With a single viable option it changes nothing (there is no second-best to take).
  const wrong = cfg.ai.WRONG_BALL_P * prof.wrongBallP;
  if (wrong > 0 && second !== null && rngFloat(rng) < wrong) return second;
  return best;
}
